// frontend/src/context/PriceContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import priceServiceInstance from '../services/PriceService';

const PriceContext = createContext(null);

export const PriceProvider = ({ children }) => {
  return (
    <PriceContext.Provider value={priceServiceInstance}>
      {children}
    </PriceContext.Provider>
  );
};

/**
 * Optimized usePrice hook.
 * Only triggers a re-render when the specific symbol's data changes.
 */
export const usePrice = (symbol) => {
  const service = useContext(PriceContext);
  const [data, setData] = useState({ 
    price: null, 
    lastDayClose: null, 
    marketStatus: null 
  });

  useEffect(() => {
    if (!symbol || !service) return;

    const handleUpdate = (newData) => {
      setData(prev => {
        const merged = {
          price: newData.price !== undefined ? newData.price : prev.price,
          lastDayClose: newData.lastDayClose !== undefined ? newData.lastDayClose : prev.lastDayClose,
          marketStatus: newData.marketStatus !== undefined ? newData.marketStatus : prev.marketStatus
        };

        if (
          prev.price === merged.price && 
          prev.lastDayClose === merged.lastDayClose &&
          prev.marketStatus === merged.marketStatus
        ) {
          return prev;
        }
        return merged;
      });
    };

    service.subscribe(symbol, handleUpdate);
    return () => service.unsubscribe(symbol, handleUpdate);
  }, [symbol, service]);

  return data;
};

/**
 * Optimized usePrices hook for multiple symbols (e.g., watchlist).
 * Each symbol update is isolated to prevent massive re-renders.
 */
export const usePrices = (symbols) => {
  const service = useContext(PriceContext);
  const [prices, setPrices] = useState({});

  useEffect(() => {
    if (!symbols || !symbols.length || !service) return;

    const symbolList = Array.isArray(symbols) ? symbols : [symbols];

    const handleUpdate = (symbol, data) => {
      if (data.price !== undefined) {
        setPrices(prev => {
          if (prev[symbol] === data.price) return prev;
          return { ...prev, [symbol]: data.price };
        });
      }
    };

    const unsubscribers = symbolList.map(sym => {
      const cb = (data) => handleUpdate(sym, data);
      service.subscribe(sym, cb);
      return () => service.unsubscribe(sym, cb);
    });

    return () => unsubscribers.forEach(unsub => unsub());
  }, [symbols, service]);

  return prices;
};
